import React, { SVGProps, useLayoutEffect, useRef, useState } from "react";

import { ChevronDown } from "../../components/Icons";

import { CARD_SURFACE_STYLE, DashboardWindow } from "./dashboardHelpers";
import { ToggleSwitch } from "./ToggleSwitch";
import { useDismissOnOutside } from "./useDismissOnOutside";

/**
 * Shared control primitives for the block edit popovers.
 *
 * Geometry from the Figma edit popover (node 6837:31940): 32px control rows, 13px secondary
 * labels, 4px radii. Colors stay on the theme tokens rather than the design's literal hexes
 * so the popover keeps theming with the dashboard.
 *
 * Spacing is written in absolute px, not rem utilities (h-8, px-3, gap-1.5, ...): the
 * dashboard iframe sets a 14px root, so every rem utility would render at 87.5% while the
 * popover width stays an absolute 280px — the popover would come out the designed width but
 * vertically compressed.
 */
export const ROW_CLASS = "flex items-center justify-between gap-[6px] h-[32px]";
export const ROW_LABEL_CLASS =
  "flex-1 min-w-0 text-[13px] leading-[18px] font-medium text-card-muted-foreground";
export const FIELD_LABEL_CLASS =
  "block text-[13px] leading-[18px] font-medium text-card-muted-foreground mb-[4px]";
export const FIELD_INPUT_CLASS =
  "w-full h-[32px] border border-card-foreground/20 rounded-[4px] px-[8px] text-[14px] " +
  "leading-[18px] text-card-foreground bg-card-background caret-primary-button-background " +
  "focus:outline-none focus:ring-2 focus:ring-primary-button-background focus:border-transparent " +
  "placeholder:text-card-muted-foreground";
/** Same as FIELD_INPUT_CLASS minus the fixed height, so a textarea can hug its content. */
export const FIELD_TEXTAREA_CLASS =
  "w-full border border-card-foreground/20 rounded-[4px] px-[8px] py-[7px] text-[14px] " +
  "leading-[18px] text-card-foreground bg-card-background caret-primary-button-background " +
  "focus:outline-none focus:ring-2 focus:ring-primary-button-background focus:border-transparent " +
  "placeholder:text-card-muted-foreground resize-none overflow-hidden";
/**
 * Dismiss affordance in a popover header: the glyph sits in a 24px tinted circle.
 *
 * The Figma draws that circle as #F5F5F5 with a #595959 glyph — light-theme greys that would
 * stay light on a dark dashboard, so it is a tint of the card foreground here instead, like
 * the rest of these controls.
 */
export const CLOSE_BUTTON_CLASS =
  "flex items-center justify-center w-[24px] h-[24px] shrink-0 rounded-full " +
  "bg-card-foreground/5 text-card-muted-foreground cursor-pointer transition-colors " +
  "hover:bg-card-foreground/10 hover:text-card-foreground";
/**
 * Hover label for an icon-only control (Figma `tooltip/right`, node 7323:6650).
 *
 * Deliberately not themed — #111215 with white text on every palette, the same choice the
 * existing hint bubbles make with bg-black/85. A tooltip reads as an overlay above the UI
 * rather than part of the card, and a theme-tinted one loses contrast on mid-tone palettes.
 *
 * Wraps within a max width instead of the design's nowrap: the same slot carries the
 * explanations for disabled choices, which are a sentence rather than a chart name.
 */
export const ICON_TOOLTIP_CLASS =
  "pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-[6px] z-20 " +
  "w-max max-w-[200px] rounded-[6px] bg-[#111215] p-[6px] text-center text-[14px] " +
  "font-semibold leading-[18px] text-white opacity-0 transition-opacity " +
  "group-hover/choice:opacity-100";
export const BUTTON_CLASS =
  "flex items-center justify-center h-[32px] px-[20px] rounded-[4px] text-[13px] leading-[18px] " +
  "font-semibold cursor-pointer transition-colors";

/** A labeled 32px control row. The container's 8px gap is the only separator — no rules. */
export const EditRow: React.FC<{
  label: string;
  /** Optional hint icon + hover bubble, rendered after the label. */
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div className={ROW_CLASS}>
    {hint ? (
      <span className={`${ROW_LABEL_CLASS} flex items-center gap-[4px]`}>
        {label}
        {/* Styled bubble rather than a native title tooltip, matching the metric-card
            error tooltip approach. */}
        <span className="group/hint relative inline-flex">
          <span className="material-icons !text-[14px] text-card-muted-foreground cursor-help">
            info_outline
          </span>
          <span className="pointer-events-none absolute left-0 bottom-full mb-[6px] w-[240px] rounded-[4px] bg-black/85 px-[8px] py-[6px] text-[12px] font-medium leading-[16px] text-white opacity-0 transition-opacity group-hover/hint:opacity-100 z-20">
            {hint}
          </span>
        </span>
      </span>
    ) : (
      <span className={`${ROW_LABEL_CLASS} truncate`}>{label}</span>
    )}
    {children}
  </div>
);

export const EditTextField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className={FIELD_LABEL_CLASS}>{label}</label>
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={FIELD_INPUT_CLASS}
    />
  </div>
);

/** Auto-growing textarea: hugs its content instead of a fixed box with a scrollbar. */
export const EditTextArea: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder }) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Reset to auto before measuring, so deleting text shrinks the box as well as growing it.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <div>
      <label className={FIELD_LABEL_CLASS}>{label}</label>
      <textarea
        ref={ref}
        rows={1}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={FIELD_TEXTAREA_CLASS}
      />
    </div>
  );
};

/** Label + pill switch. */
export const EditToggleRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <EditRow label={label}>
    <ToggleSwitch checked={checked} onChange={onChange} />
  </EditRow>
);

export interface SelectOption<V extends string = string> {
  value: V;
  label: string;
  icon?: string;
  iconRotate?: boolean;
}

/**
 * Compact value dropdown: current value + chevron, right-aligned menu, sized to the same
 * Figma geometry as the rest of the popover.
 *
 * Dismissal mirrors MetricIconPicker — capture phase, so an open menu closes in its own step
 * without tearing down the whole edit popover (see useDismissOnOutside).
 */
export function PopoverSelect<V extends string = string>({
  value,
  options,
  onChange,
  targetWindow,
  ariaLabel,
  triggerLabel,
  menuHeader,
}: {
  value: V;
  options: SelectOption<V>[];
  onChange: (value: V) => void;
  targetWindow: DashboardWindow;
  ariaLabel: string;
  /** Trigger text when the current value is deliberately not among the options. */
  triggerLabel?: string;
  /** Small muted caption at the top of the open menu (context shown only when needed). */
  menuHeader?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useDismissOnOutside(containerRef, () => setIsOpen(false), targetWindow, {
    enabled: isOpen,
    capture: true,
  });

  const currentLabel = triggerLabel ?? options.find(o => o.value === value)?.label ?? value;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
        className="flex items-center gap-[2px] text-[13px] leading-[18px] font-medium text-card-foreground cursor-pointer hover:text-card-foreground/80 transition-colors"
      >
        {currentLabel}
        <ChevronDown
          width={16}
          height={16}
          className={`text-card-muted-foreground transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {isOpen && (
        <div
          className="absolute right-0 top-full mt-[4px] z-10 min-w-[140px] rounded-[8px] border border-card-border shadow-xl py-[4px]"
          style={CARD_SURFACE_STYLE}
        >
          {menuHeader && (
            <div className="px-[12px] pt-[2px] pb-[6px] mb-[4px] text-[12px] leading-[16px] text-card-muted-foreground border-b border-card-foreground/10 whitespace-nowrap">
              {menuHeader}
            </div>
          )}
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              // One weight for every row. Bumping the selected one to font-medium at full
              // opacity read as a larger font beside its 400-weight neighbours, so selection
              // is carried by contrast alone — the trigger already names the current value.
              aria-current={option.value === value}
              className={`flex w-full items-center gap-[8px] text-left px-[12px] py-[6px] text-[13px] leading-[18px] cursor-pointer hover:bg-card-foreground/10 ${
                option.value === value ? "text-card-foreground" : "text-card-foreground/70"
              }`}
            >
              {option.icon && (
                <span
                  className={`material-icons !text-[16px] text-card-muted-foreground shrink-0 ${
                    option.iconRotate ? "rotate-90" : ""
                  }`}
                >
                  {option.icon}
                </span>
              )}
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Label + dropdown row. */
export function EditSelectRow<V extends string = string>({
  label,
  value,
  options,
  onChange,
  targetWindow,
  ariaLabel,
}: {
  label: string;
  value: V;
  options: SelectOption<V>[];
  onChange: (value: V) => void;
  targetWindow: DashboardWindow;
  ariaLabel: string;
}) {
  return (
    <EditRow label={label}>
      <PopoverSelect
        value={value}
        options={options}
        onChange={onChange}
        targetWindow={targetWindow}
        ariaLabel={ariaLabel}
      />
    </EditRow>
  );
}

export interface IconChoice<V extends string = string> {
  value: V;
  /** Icon component, sized by the row. */
  Icon?: React.FC<SVGProps<Element>>;
  /**
   * Material icon name, for choices without designed artwork (the AI-convert targets —
   * only the seven switchable types have drawn icons). Ignored when `Icon` is set.
   */
  materialIcon?: string;
  /** Rotate the material glyph 90° — horizontalBar reuses bar_chart. */
  materialRotate?: boolean;
  /** Hover text. When disabled, say why — the button can't explain itself. */
  title: string;
  disabled?: boolean;
}

/**
 * Exclusive choice rendered as a row of icon buttons (the chart type switcher).
 *
 * Per the Figma chart popover the targets are 32px with 12px gaps. Seven of them come to
 * 7x32 + 6x12 = 296px, exactly the 320px popover's content width, so the label sits on its own
 * line above them rather than sharing a 32px row — the design's five fit beside a label, this
 * branch's seven do not.
 */
export function EditIconChoiceRow<V extends string = string>({
  label,
  hint,
  value,
  choices,
  onChange,
}: {
  label: string;
  /** Optional hint icon + hover bubble after the label, same treatment as EditRow's. */
  hint?: string;
  value: string;
  choices: IconChoice<V>[];
  onChange: (value: V) => void;
}) {
  return (
    <div>
      <label className={FIELD_LABEL_CLASS}>
        {hint ? (
          <span className="flex items-center gap-[4px]">
            {label}
            <span className="group/hint relative inline-flex">
              <span className="material-icons !text-[14px] text-card-muted-foreground cursor-help">
                info_outline
              </span>
              <span className="pointer-events-none absolute left-0 bottom-full mb-[6px] w-[240px] rounded-[4px] bg-black/85 px-[8px] py-[6px] text-[12px] font-medium leading-[16px] text-white opacity-0 transition-opacity group-hover/hint:opacity-100 z-20">
                {hint}
              </span>
            </span>
          </span>
        ) : (
          label
        )}
      </label>
      <div className="flex items-center gap-[12px]">
        {choices.map(choice => {
          const isSelected = choice.value === value;
          return (
            // The hover target is this wrapper, not the button: a disabled button fires no
            // pointer events in Chrome, so a tooltip hung off the button itself would never
            // appear on exactly the choices whose label explains why they are unavailable.
            <span key={choice.value} className="group/choice relative inline-flex shrink-0">
              <button
                type="button"
                // Carries the accessible name these icon-only buttons would otherwise lack —
                // it was riding on the `title` the tooltip replaces.
                aria-label={choice.title}
                // The E2E hook. The accessible name is prose that restates the design's
                // wording and changes with the disabled reason, so it cannot also be the
                // handle the tests hold a choice by.
                data-choice={choice.value}
                aria-pressed={isSelected}
                disabled={choice.disabled}
                onClick={() => onChange(choice.value)}
                className={`flex items-center justify-center w-[32px] h-[32px] rounded-[4px] shrink-0 transition-colors ${
                  isSelected
                    ? "bg-primary-button-background/15 text-primary-button-background cursor-pointer"
                    : choice.disabled
                    ? "text-card-muted-foreground/40 cursor-not-allowed"
                    : "text-card-muted-foreground hover:bg-card-foreground/10 hover:text-card-foreground cursor-pointer"
                }`}
              >
                {choice.Icon ? (
                  <choice.Icon width={32} height={32} />
                ) : (
                  <span
                    className={`material-icons !text-[22px] ${
                      choice.materialRotate ? "rotate-90" : ""
                    }`}
                  >
                    {choice.materialIcon}
                  </span>
                )}
              </button>
              <span className={ICON_TOOLTIP_CLASS}>{choice.title}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}
